
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TestResult } from "@/types/results";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { useDeletePuttingResult } from "@/hooks/useDeletePuttingResult";
import { useTranslation } from "react-i18next";
import { useState } from "react";

type TestHistoryProps = {
  results: TestResult[];
  title: string;
  situationIds: number[];
};

export const TestHistory = ({ results, title, situationIds }: TestHistoryProps) => {
  const { t } = useTranslation('results');
  const { deletePuttingResult, isDeleting } = useDeletePuttingResult();
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());
  const filteredResults = results.filter(r => situationIds.includes(r.situation_id));
  
  // Grouper les résultats par test (même date et situation)
  const groupedTests = filteredResults.reduce((acc, result) => {
    const testKey = `${result.situation_id}-${result.test_date}`;
    if (!acc[testKey]) {
      acc[testKey] = [];
    }
    acc[testKey].push(result);
    return acc;
  }, {} as Record<string, TestResult[]>);
  
  const toggleExpand = (testKey: string) => {
    setExpandedTests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testKey)) {
        newSet.delete(testKey);
      } else {
        newSet.add(testKey);
      }
      return newSet;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl text-golf-700">{t('history.title')} - {title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>{t('history.date')}</TableHead>
              <TableHead>{t('history.situation')}</TableHead>
              <TableHead className="text-right">{t('history.points')}</TableHead>
              <TableHead className="text-right">{t('history.index')}</TableHead>
              <TableHead className="w-[100px]">{t('history.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(groupedTests)
              .sort(([, a], [, b]) => new Date(b[0].test_date).getTime() - new Date(a[0].test_date).getTime())
              .map(([testKey, testResults]) => {
                const totalPoints = testResults.reduce((sum, r) => sum + r.points, 0);
                const avgIndex = Math.round(testResults.reduce((sum, r) => sum + r.index, 0) / testResults.length);
                const isExpanded = expandedTests.has(testKey);
                const firstResult = testResults[0];
                
                // Trier les résultats par point (A, B, C, D, E)
                const sortedResults = [...testResults].sort((a, b) => {
                  const pointA = (a as any).point || 'Z';
                  const pointB = (b as any).point || 'Z';
                  return pointA.localeCompare(pointB);
                });
                
                return (
                  <>
                    <TableRow key={testKey} className="cursor-pointer hover:bg-muted/50">
                      <TableCell onClick={() => toggleExpand(testKey)}>
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell onClick={() => toggleExpand(testKey)}>
                        {format(new Date(firstResult.test_date), "d MMMM yyyy HH:mm", { locale: fr })}
                      </TableCell>
                      <TableCell onClick={() => toggleExpand(testKey)}>
                        {t(`situations.situation_${firstResult.situation_id}`)}
                      </TableCell>
                      <TableCell className="text-right font-semibold" onClick={() => toggleExpand(testKey)}>
                        {totalPoints}
                      </TableCell>
                      <TableCell className="text-right font-semibold" onClick={() => toggleExpand(testKey)}>
                        {avgIndex}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => testResults.forEach(r => deletePuttingResult(r.id))}
                          disabled={isDeleting}
                          className="hover:bg-destructive/20 hover:text-destructive"
                          title="Supprimer tout le test"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isExpanded && sortedResults.map((result, idx) => (
                      <TableRow key={result.id} className="bg-muted/20">
                        <TableCell></TableCell>
                        <TableCell className="pl-8 text-sm text-muted-foreground">
                          {t('table.point')} {(result as any).point || String.fromCharCode(65 + idx)}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right text-sm">{result.points}</TableCell>
                        <TableCell className="text-right text-sm">{result.index}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePuttingResult(result.id)}
                            disabled={isDeleting}
                            className="hover:bg-destructive/20 hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                );
              })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
